import { Search } from '@mui/icons-material'
import { Alert, Box, FormControlLabel, Grid } from '@mui/material'
import {
  Button,
  Card,
  CardHeader,
  DatePickerC,
  SelectC,
  SwitchC,
  TextFieldC
} from 'components'
import { parse } from 'date-fns'
import { queryClient } from 'lib'
import { useCheckCnpjCpf } from 'modules/customers/hooks'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  loadCities,
  loadStates,
  useLoadCustomerRegistrationByCnpj,
  useLoadUnities
} from 'services'
import {
  cellPhoneMask,
  cnpjCpfMask,
  cnpjCpfToString,
  cnpjValidator,
  cpfValidator,
  useDebounce,
  Yup
} from 'utils'

export const GeneralSchema = Yup.object({
  personType: Yup.object({
    id: Yup.string().required(),
    label: Yup.string().required()
  }).required(),
  unity: Yup.object({
    id: Yup.string().required(),
    label: Yup.string().required()
  }).nullable(),
  fullName: Yup.string().required().max(60),
  nickname: Yup.string().required().max(20),
  cnpjCpf: Yup.string()
    .required()
    .min(11)
    .max(18)
    .test('cnpjCpf-valid', 'CNPJ/CPF inválido', function (value) {
      if (!value) return false
      const personType = this.parent.personType
      const isLegalPerson = personType?.id === '0'
      return isLegalPerson
        ? cnpjValidator.isValid(value)
        : cpfValidator.isValid(value)
    }),
  registration: Yup.string().max(20),
  registrationExempt: Yup.boolean(),
  observation: Yup.string().max(80),
  birthDate: Yup.string(),
  phone: Yup.string().required().max(20)
})

export type GeneralType = {
  personType: { id: string; label: string } | null
  unity: { id: string; label: string } | null
  fullName: string
  nickname: string
  cnpjCpf: string
  registration: string
  registrationExempt: boolean
  originContact: { id: string; label: string } | null
  operatingSegment: { id: string; label: string } | null
  seller: { id: string; label: string } | null
  observation: string
  birthDate: string | null
  phone: string
}

export const generalDefaultValues = {
  personType: { id: '0', label: 'Juridica' },
  unity: null,
  fullName: '',
  nickname: '',
  cnpjCpf: '',
  registration: '',
  registrationExempt: undefined,
  originContact: null,
  operatingSegment: null,
  seller: null,
  observation: '',
  birthDate: '',
  phone: ''
}

export const GeneralForm = () => {
  const { watch, setValue } = useFormContext()
  const isLegalPerson = watch('general.personType')?.id === '0'
  const cnpjCpf = watch('general.cnpjCpf')

  const isExempt = !!watch('general.registrationExempt')
  const { data: unityData, isLoading: unityLoading } = useLoadUnities()
  const { mutateAsync: searchCnpj } = useLoadCustomerRegistrationByCnpj()

  const cnpjCpfValue = useMemo(() => {
    return cnpjCpf ? cnpjCpfToString(cnpjCpf) : ''
  }, [cnpjCpf])

  const debouncedCnpjCpf = useDebounce(cnpjCpfValue, 500)

  const { data: checkCnpjCpfData } = useCheckCnpjCpf(
    debouncedCnpjCpf,
    !!debouncedCnpjCpf && debouncedCnpjCpf.length >= 11
  )

  const checkMessage = checkCnpjCpfData?.message
  const hasCustomerExists = !!checkMessage

  useEffect(() => {
    setValue('_customerExists', hasCustomerExists, { shouldValidate: false })
  }, [hasCustomerExists, setValue])

  const unityOptions = useMemo(() => {
    return (
      unityData?.data?.map((unity: any) => ({
        id: unity.id,
        label: `${unity.id} - ${unity.shortName}`
      })) ?? []
    )
  }, [unityData])

  useEffect(() => {
    if (isExempt) {
      setValue('general.registration', 'Isento')
    } else {
      setValue('general.registration', '')
    }
  }, [isExempt, setValue])

  const handleSearchCnpj = async () => {
    const cnpj = watch('general.cnpjCpf')
    const cnpjValue = cnpjCpfToString(cnpj)

    try {
      const searchData = await searchCnpj(cnpjValue)

      // console.log(searchData)
      if (searchData.birthDate) {
        setValue(
          'general.birthDate',
          parse(searchData.birthDate, 'dd/MM/yyyy', new Date())
        )
      }
      setValue('general.fullName', searchData.name)
      setValue('general.phone', searchData.phone)
      if (searchData.registration && !isExempt) {
        setValue('general.registration', searchData.registration)
      }
      setValue('contact.email', searchData.email)
      setValue('contact.phone', searchData.phone)
      setValue('address.zipCode', searchData.address.zipCode)
      setValue('address.street', searchData.address.lineOne)
      setValue('address.number', searchData.address.number)
      setValue('address.district', searchData.address.district)
      setValue('address.complement', searchData.address.complement)

      const stateId = searchData.address.state
      setValue('address.state', { id: stateId })

      // ensure the cities for that state are loaded in cache
      const citiesResponse = await queryClient.fetchQuery(
        ['cities', { stateId }],
        () => loadCities({ stateId })
      )

      const statesResponse = await queryClient.fetchQuery(['states'], () =>
        loadStates({ stateId })
      )
      const normalizedState = statesResponse?.data.find(
        (s: any) => s.id === stateId
      )

      // console.log(normalizedState, statesResponse)
      if (normalizedState)
        setValue('address.state', {
          id: normalizedState.id,
          name: normalizedState.name
        })

      // find the city from the preloaded list
      const normalized = searchData.address.city
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()

      const match = citiesResponse.data.find(
        (c: any) =>
          c.name
            ?.normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase() === normalized
      )

      if (match) {
        setValue('address.city', { id: match.id, name: match.name })
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div>
      <Card
        sx={{
          visibility: { xs: 'hidden', sm: 'visible', md: 'visible' },
          padding: { xs: 0, sm: '24px', md: '24px' }
          // height: { md: '632px' }
        }}
      >
        <Box
          sx={{
            visibility: { xs: 'visible', sm: 'visible', md: 'visible' }
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              visibility: 'visible'
            }}
          >
            <CardHeader title="Informações Gerais" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectC
                  name="general.personType"
                  options={[
                    { id: '0', label: 'Jurídica' },
                    { id: '1', label: 'Física' }
                  ]}
                  label="Tipo Pessoa"
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC name="general.fullName" label="Nome do Cliente" />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC name="general.nickname" label="Fantasia/Apelido" />
              </Grid>
              <Grid item xs={isLegalPerson ? 8 : 12}>
                <TextFieldC
                  mask={isLegalPerson ? '00.000.000/0000-00' : '000.000.000-00'}
                  name="general.cnpjCpf"
                  label="CNPJ/CPF"
                  placeholder={
                    isLegalPerson ? '00.000.000/0000-00' : '000.000.000-00'
                  }
                  normalize={(value) => cnpjCpfMask(value)}
                />
              </Grid>
              {isLegalPerson && (
                <Grid item xs={4}>
                  <Button variant="outlined" onClick={handleSearchCnpj}>
                    <Search />
                  </Button>
                </Grid>
              )}
              {hasCustomerExists && (
                <Grid item xs={12}>
                  <Alert severity="warning">{checkMessage}</Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <DatePickerC
                  name="general.birthDate"
                  label={
                    isLegalPerson ? 'Fundação/Nascimento' : 'Data de Nascimento'
                  }
                  inputFormat="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldC
                  name="general.phone"
                  label="Telefone"
                  normalize={(value) => cellPhoneMask(value)}
                  placeholder="(00) 0 0000-0000"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldC
                  name="general.registration"
                  label="Inscrição Estadual"
                  disabled={isExempt}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <SwitchC
                      label="Isento?"
                      name="general.registrationExempt"
                      checked={isExempt}
                      onChange={(e: any) =>
                        setValue('general.registrationExempt', e.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </Grid>
              <Grid item xs={12}>
                <SelectC
                  name="general.unity"
                  label="Unidade de Negócio"
                  options={unityOptions}
                  loading={unityLoading}
                  getOptionLabel={(option: any) => option.label || ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC
                  name="general.observation"
                  label="Observação"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </div>
        </Box>
      </Card>
    </div>
  )
}
